import React, { useState, useEffect, useRef, useCallback } from "react";
import { waStatus, waLink, waPair, waLogout, waDeleted, waMediaUrl, waTranslate, waDismiss, waSend, waRecipients, waInstruct, API, BACKEND_URL } from "../lib/api";
import { useAudio } from "./AudioProvider";
import { MessageSquareX, QrCode, LogOut, RefreshCw, Loader2, Volume2, Languages, Image as ImageIcon, Mic, Film, FileText, Trash2, KeyRound, Laptop, Download, Share2, Check, Send, Users } from "lucide-react";
import { toast } from "sonner";

function fmtTime(ts) {
  try { return new Date(ts).toLocaleString(); } catch { return ""; }
}

function fmtAgo(secs) {
  if (secs == null) return "";
  if (secs < 5) return "just now";
  if (secs < 60) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  return `${Math.round(secs / 3600)} h ago`;
}

const TYPE_ICON = { image: ImageIcon, voice: Mic, audio: Mic, video: Film, document: FileText };

// Common country dial codes (searchable via native select on mobile).
const COUNTRIES = [
  { c: "IN", n: "India", d: "91" }, { c: "US", n: "United States", d: "1" },
  { c: "GB", n: "United Kingdom", d: "44" }, { c: "AE", n: "UAE", d: "971" },
  { c: "CA", n: "Canada", d: "1" }, { c: "AU", n: "Australia", d: "61" },
  { c: "PK", n: "Pakistan", d: "92" }, { c: "BD", n: "Bangladesh", d: "880" },
  { c: "SA", n: "Saudi Arabia", d: "966" }, { c: "QA", n: "Qatar", d: "974" },
  { c: "KW", n: "Kuwait", d: "965" }, { c: "OM", n: "Oman", d: "968" },
  { c: "BH", n: "Bahrain", d: "973" }, { c: "SG", n: "Singapore", d: "65" },
  { c: "MY", n: "Malaysia", d: "60" }, { c: "ZA", n: "South Africa", d: "27" },
  { c: "NZ", n: "New Zealand", d: "64" }, { c: "IE", n: "Ireland", d: "353" },
  { c: "DE", n: "Germany", d: "49" }, { c: "FR", n: "France", d: "33" },
  { c: "IT", n: "Italy", d: "39" }, { c: "ES", n: "Spain", d: "34" },
  { c: "NL", n: "Netherlands", d: "31" }, { c: "BE", n: "Belgium", d: "32" },
  { c: "CH", n: "Switzerland", d: "41" }, { c: "SE", n: "Sweden", d: "46" },
  { c: "NO", n: "Norway", d: "47" }, { c: "DK", n: "Denmark", d: "45" },
  { c: "PT", n: "Portugal", d: "351" }, { c: "PL", n: "Poland", d: "48" },
  { c: "TR", n: "Turkey", d: "90" }, { c: "RU", n: "Russia", d: "7" },
  { c: "CN", n: "China", d: "86" }, { c: "JP", n: "Japan", d: "81" },
  { c: "KR", n: "South Korea", d: "82" }, { c: "ID", n: "Indonesia", d: "62" },
  { c: "PH", n: "Philippines", d: "63" }, { c: "TH", n: "Thailand", d: "66" },
  { c: "VN", n: "Vietnam", d: "84" }, { c: "LK", n: "Sri Lanka", d: "94" },
  { c: "NP", n: "Nepal", d: "977" }, { c: "EG", n: "Egypt", d: "20" },
  { c: "NG", n: "Nigeria", d: "234" }, { c: "KE", n: "Kenya", d: "254" },
  { c: "BR", n: "Brazil", d: "55" }, { c: "MX", n: "Mexico", d: "52" },
  { c: "AR", n: "Argentina", d: "54" }, { c: "IL", n: "Israel", d: "972" },
];

function DeletedCard({ msg, userId, uiLang, onDismiss }) {
  const { speak } = useAudio();
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const Icon = TYPE_ICON[msg.type];

  const doTranslate = async () => {
    setTranslating(true);
    try {
      const res = await waTranslate(msg.msg_id, userId, uiLang || "en");
      setTranslation(res.translation || "(no text)");
    } catch (e) { toast.error("Could not translate"); }
    finally { setTranslating(false); }
  };

  const readAloud = () => {
    const text = translation || msg.text;
    if (text) speak(text, 1.0, `wa-${msg.msg_id}`);
    else toast.message("No text to read in this message");
  };

  const forward = async () => {
    setSharing(true);
    const text = [msg.sender_name ? `From ${msg.sender_name}:` : "", translation || msg.text || ""].filter(Boolean).join("\n");
    try {
      // Try to share the actual media file (best on WhatsApp) if present.
      if (msg.media_path && navigator.canShare) {
        const resp = await fetch(waMediaUrl(msg.msg_id, userId));
        const blob = await resp.blob();
        const extMap = { "image/jpeg": "jpg", "image/png": "png", "audio/ogg": "ogg", "audio/mpeg": "mp3", "video/mp4": "mp4", "application/pdf": "pdf" };
        const ext = extMap[blob.type] || (msg.type === "image" ? "jpg" : "bin");
        const file = new File([blob], `whatsapp-${msg.msg_id}.${ext}`, { type: blob.type || "application/octet-stream" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: text || undefined });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ text: text || "WhatsApp message" });
        return;
      }
      throw new Error("share-unsupported");
    } catch (e) {
      if (e && e.name === "AbortError") return; // user cancelled the share sheet
      // Fallbacks: text -> WhatsApp web composer; media -> copy link.
      if (text) {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      } else if (msg.media_path) {
        try { await navigator.clipboard.writeText(waMediaUrl(msg.msg_id, userId)); toast.success("Media link copied — paste it into WhatsApp"); }
        catch { window.open(waMediaUrl(msg.msg_id, userId), "_blank"); }
      } else {
        toast.message("Nothing to forward in this message");
      }
    } finally { setSharing(false); }
  };

  const markDone = async () => {
    setDismissing(true);
    try {
      await waDismiss(msg.msg_id, userId);
      onDismiss(msg.msg_id);
      toast.success("Marked as read & removed");
    } catch (e) { toast.error("Could not remove"); setDismissing(false); }
  };

  return (
    <div className="bg-white rounded-2xl border-3 border-[#1A1C19] p-4 space-y-3" data-testid={`wa-deleted-${msg.msg_id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-extrabold truncate">{msg.sender_name || "Unknown"}</p>
          <p className="text-xs text-[#50564F]">{fmtTime(msg.timestamp)}{msg.is_group ? " · group" : ""}</p>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-bold bg-[#DC2626] text-white px-2 py-1 rounded-full shrink-0">
          <Trash2 className="w-3 h-3" /> deleted
        </span>
      </div>

      {msg.text ? (
        <p className="text-base bg-[#F5F5F0] rounded-xl p-3 whitespace-pre-wrap break-words">{msg.text}</p>
      ) : (
        <p className="text-sm text-[#50564F] flex items-center gap-2">{Icon ? <Icon className="w-4 h-4" /> : null} {msg.type} message</p>
      )}

      {msg.media_path && msg.type === "image" && (
        <img src={waMediaUrl(msg.msg_id, userId)} alt="deleted media" className="rounded-xl border-2 border-[#1A1C19] max-h-72 object-contain" data-testid={`wa-media-${msg.msg_id}`} />
      )}
      {msg.media_path && (msg.type === "voice" || msg.type === "audio") && (
        <audio controls src={waMediaUrl(msg.msg_id, userId)} className="w-full" data-testid={`wa-audio-${msg.msg_id}`} />
      )}
      {msg.media_path && msg.type === "video" && (
        <video controls src={waMediaUrl(msg.msg_id, userId)} className="w-full rounded-xl border-2 border-[#1A1C19] max-h-72" data-testid={`wa-video-${msg.msg_id}`} />
      )}
      {msg.media_path && msg.type === "document" && (
        <a href={waMediaUrl(msg.msg_id, userId)} target="_blank" rel="noreferrer" className="text-sm font-bold text-[#2563EB] underline">Open document</a>
      )}

      {translation && (
        <p className="text-base bg-[#ECFDF5] border-2 border-[#15803D] rounded-xl p-3 whitespace-pre-wrap break-words" data-testid={`wa-translation-${msg.msg_id}`}>{translation}</p>
      )}

      <div className="flex gap-2 flex-wrap">
        {msg.text && (
          <>
            <button onClick={readAloud} data-testid={`wa-read-${msg.msg_id}`} className="yb-tactile bg-[#FFD600] px-4 py-2 font-bold flex items-center gap-2">
              <Volume2 className="w-4 h-4" /> Read aloud
            </button>
            <button onClick={doTranslate} disabled={translating} data-testid={`wa-translate-${msg.msg_id}`} className="yb-tactile bg-white px-4 py-2 font-bold flex items-center gap-2">
              {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />} Translate
            </button>
          </>
        )}
        <button onClick={forward} disabled={sharing} data-testid={`wa-forward-${msg.msg_id}`} className="yb-tactile bg-[#25D366] text-white px-4 py-2 font-bold flex items-center gap-2">
          {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Forward
        </button>
        <button onClick={markDone} disabled={dismissing} data-testid={`wa-done-${msg.msg_id}`} className="yb-tactile bg-[#1A1C19] text-white px-4 py-2 font-bold flex items-center gap-2 ml-auto">
          {dismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Done
        </button>
      </div>
    </div>
  );
}

function WASendComposer({ userId }) {
  const [instruction, setInstruction] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null); // { recipient, phone, message, found, linked }

  const makeDraft = async () => {
    if (!instruction.trim()) { toast.error("Tell Nexus what to send"); return; }
    setDrafting(true); setDraft(null);
    try {
      const d = await waInstruct(userId, instruction.trim());
      setDraft(d);
      if (!d.found) toast.message("Nexus drafted it — check the recipient below, it wasn't a clear match.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nexus couldn't work that out");
    } finally { setDrafting(false); }
  };

  const send = async () => {
    if (!draft?.message?.trim()) { toast.error("Nothing to send"); return; }
    if (!draft?.phone) { toast.error("No number for that person — add them to contacts first"); return; }
    setSending(true);
    try {
      await waSend(userId, { to: draft.phone, name: draft.recipient, text: draft.message });
      toast.success(`Sent to ${draft.recipient} — Nexus is delivering it`);
      setInstruction(""); setDraft(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-2xl border-3 border-[#1A1C19] p-4 space-y-3" data-testid="wa-send-composer">
      <div className="flex items-center gap-2">
        <Send className="w-6 h-6 text-[#25D366]" />
        <p className="font-extrabold text-lg">Tell Nexus to send a WhatsApp</p>
      </div>
      <p className="text-xs text-[#50564F]">Just say what you want in plain words — Nexus finds the person and writes it in your voice. e.g. <i>"Message Nafisa I'm running late, home by 8"</i> or <i>"Wish Anas good luck for his exam"</i>.</p>
      <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={2}
        placeholder='"Tell Nafisa dinner is at 8" · "Ask Hussain to call me"' data-testid="wa-instruct-input"
        className="yb-tactile w-full bg-white px-3 py-3 outline-none resize-none" />
      <button onClick={makeDraft} disabled={drafting} data-testid="wa-instruct-btn"
        className="yb-tactile w-full bg-[#1A1C19] text-white py-3 font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {drafting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Draft with Nexus
      </button>

      {draft && (
        <div className="bg-[#F0FDF4] border-2 border-[#15803D] rounded-xl p-3 space-y-2" data-testid="wa-draft-card">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-extrabold text-[#15803D]">To:</span>
            <span className="font-bold">{draft.recipient || "?"}</span>
            {draft.phone ? <span className="text-xs text-[#50564F]">({draft.phone})</span>
              : <span className="text-xs text-[#B91C1C] font-bold">no saved number</span>}
          </div>
          <textarea value={draft.message} onChange={(e) => setDraft({ ...draft, message: e.target.value })} rows={3}
            data-testid="wa-draft-message" className="yb-tactile w-full bg-white px-3 py-3 outline-none resize-none text-base" />
          {!draft.linked && <p className="text-xs text-[#B91C1C] font-bold">⚠ WhatsApp isn't linked yet — link your number below first, and make sure your laptop capture app is running.</p>}
          <div className="flex gap-2">
            <button onClick={send} disabled={sending || !draft.phone} data-testid="wa-draft-send"
              className="yb-tactile flex-1 bg-[#25D366] text-white py-3 font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} Send it
            </button>
            <button onClick={() => setDraft(null)} data-testid="wa-draft-cancel"
              className="yb-tactile bg-white px-4 py-3 font-bold border-2 border-[#1A1C19]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhatsApp({ session }) {
  const userId = session.user_id;
  const [status, setStatus] = useState("loading");
  const [qr, setQr] = useState(null);
  const [me, setMe] = useState(null);
  const [deleted, setDeleted] = useState([]);
  const [linking, setLinking] = useState(false);
  const [phone, setPhone] = useState(() => localStorage.getItem("wa_phone") || "");
  const [dial, setDial] = useState(() => localStorage.getItem("wa_dial") || "91");
  const [showQr, setShowQr] = useState(false);
  const [pairCode, setPairCode] = useState(null);
  const [pairing, setPairing] = useState(false);
  const pairCodeRef = useRef(null);
  useEffect(() => { localStorage.setItem("wa_phone", phone); }, [phone]);
  useEffect(() => { localStorage.setItem("wa_dial", dial); }, [dial]);
  const [online, setOnline] = useState(false);
  const [lastSeen, setLastSeen] = useState(null);
  const [agentVer, setAgentVer] = useState(null);
  const pollRef = useRef(null);
  const CURRENT_AGENT_VER = 2;

  const refreshStatus = useCallback(async () => {
    try {
      const s = await waStatus(userId);
      setStatus(s.status);
      setQr(s.qr);
      setMe(s.me);
      setOnline(!!s.online);
      setLastSeen(s.last_seen_secs);
      if (s.online) setAgentVer(s.agent_ver ?? 0);
      if (s.pairingCode) { setPairCode(s.pairingCode); pairCodeRef.current = s.pairingCode; }
      if (s.status === "connected") loadDeleted();
    } catch (e) { setStatus("offline"); setOnline(false); }
  }, [userId]); // eslint-disable-line

  const loadDeleted = useCallback(async () => {
    try { setDeleted(await waDeleted(userId)); } catch (e) { /* ignore */ }
  }, [userId]);

  useEffect(() => {
    refreshStatus();
    pollRef.current = setInterval(refreshStatus, 4000);
    return () => clearInterval(pollRef.current);
  }, [refreshStatus]);

  const startLink = async () => {
    setLinking(true);
    try { await waLink(userId); await refreshStatus(); }
    catch (e) { toast.error("Could not start linking"); }
    finally { setLinking(false); }
  };

  const pairWithCode = async () => {
    const national = phone.replace(/\D/g, "").replace(/^0+/, ""); // strip leading zeros
    const digits = (dial + national).replace(/\D/g, "");
    if (!national) { toast.error("Enter your WhatsApp number (without the country code)"); return; }
    if (digits.length < 8) { toast.error("That number looks too short — check the country and number"); return; }
    if (!online) { toast.error("Your office laptop is offline — start the capture app there first"); return; }
    setPairing(true);
    setPairCode(null); pairCodeRef.current = null;
    try {
      const res = await waPair(userId, digits);
      if (res.pairingCode) { setPairCode(res.pairingCode); pairCodeRef.current = res.pairingCode; toast.success("Code ready — enter it in WhatsApp"); }
      else toast.message("Generating your code… it appears here in a few seconds");
      await refreshStatus();
      // Fallback: if no code arrives, the number was likely wrong — tell the user instead of leaving them stuck.
      setTimeout(() => {
        if (!pairCodeRef.current) toast.error("No code came back — double-check the country and number, then tap again");
      }, 22000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not get a code");
    } finally { setPairing(false); }
  };

  const doLogout = async () => {
    try { await waLogout(userId); setDeleted([]); setMe(null); await refreshStatus(); toast.success("Unlinked"); }
    catch (e) { toast.error("Logout failed"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="whatsapp-module">
      <div className="bg-[#25D366] rounded-2xl border-3 border-[#1A1C19] p-4 flex items-center gap-3">
        <MessageSquareX className="w-8 h-8 text-white" />
        <div>
          <p className="text-xl font-extrabold text-white">WhatsApp — Deleted Messages</p>
          <p className="text-sm text-white/90">Captures messages the instant they arrive — even if the sender deletes them.</p>
        </div>
      </div>

      <div className="bg-[#FEFCE8] rounded-2xl border-3 border-[#1A1C19] p-3 text-sm">
        <strong>Heads-up:</strong> this links your WhatsApp as a companion device (unofficial). It runs in <strong>strict read-only mode</strong> — it only listens, never sends, never appears online. This keeps the footprint minimal, but WhatsApp could still ban linked numbers, so use it at your own discretion.
      </div>

      {/* Connection health indicator */}
      <div
        data-testid="wa-health-bar"
        className={`rounded-2xl border-3 border-[#1A1C19] p-4 flex items-center gap-3 ${online ? "bg-[#ECFDF5]" : "bg-[#FEF2F2]"}`}
      >
        <div className="relative shrink-0">
          <Laptop className={`w-7 h-7 ${online ? "text-[#15803D]" : "text-[#DC2626]"}`} />
          <span
            className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${online ? "bg-[#15803D] animate-pulse" : "bg-[#DC2626]"}`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-extrabold" data-testid="wa-health-label">
            {online ? "🟢 Laptop online — capture is live" : "🔴 Laptop offline — capture app not running"}
          </p>
          <p className="text-xs text-[#50564F]" data-testid="wa-health-detail">
            {online
              ? `Last seen ${fmtAgo(lastSeen)}`
              : lastSeen != null
                ? `Last seen ${fmtAgo(lastSeen)} · start run.bat on your office laptop and keep the window open`
                : "Never connected · run run.bat on your office laptop and keep the window open"}
          </p>
        </div>
      </div>

      {/* Connection panel */}
      {status === "connected" ? (
        <div className="bg-white rounded-2xl border-3 border-[#15803D] p-4 flex items-center justify-between gap-3" data-testid="wa-connected">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-[#15803D]" />
            <div>
              <p className="font-extrabold">Connected</p>
              <p className="text-xs text-[#50564F]">{me?.id ? me.id.split(":")[0] : "linked"}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={loadDeleted} data-testid="wa-refresh" className="yb-tactile bg-white px-3 py-2 font-bold flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button onClick={doLogout} data-testid="wa-logout" className="yb-tactile bg-[#DC2626] text-white px-3 py-2 font-bold flex items-center gap-2"><LogOut className="w-4 h-4" /> Unlink</button>
          </div>
        </div>
      ) : status === "offline" ? (
        <div className="bg-[#FEF2F2] rounded-2xl border-3 border-[#DC2626] p-6 space-y-4" data-testid="wa-offline-panel">
          <div className="text-center space-y-2">
            <MessageSquareX className="w-12 h-12 mx-auto text-[#DC2626]" />
            <p className="font-extrabold text-lg">Set up the capture app (2 minutes)</p>
            <p className="text-sm text-[#50564F]">
              Deleted messages are captured by a tiny app that runs on your always-on office laptop.
              Do this once on that laptop:
            </p>
          </div>

          <a
            href={`${API}/whatsapp/download-agent?base=${encodeURIComponent(BACKEND_URL)}`}
            download
            data-testid="wa-download-btn"
            className="yb-tactile w-full bg-[#25D366] text-white py-4 font-extrabold text-lg flex items-center justify-center gap-2"
          >
            <Download className="w-6 h-6" /> Download the capture app
          </a>

          <ol className="text-sm text-[#1A1C19] space-y-2 list-decimal pl-5 bg-white rounded-2xl border-3 border-[#1A1C19] p-4">
            <li>Install <strong>Node.js LTS</strong> once from <span className="font-mono">nodejs.org</span> (skip if already installed).</li>
            <li><strong>Unzip</strong> the downloaded folder onto your Desktop (right-click → Extract All).</li>
            <li>Open the folder and double-click <strong>run.bat</strong>. <strong>Keep the black window open.</strong></li>
            <li><strong>Optional but recommended:</strong> double-click <strong>install-autostart.bat</strong> once — then capture starts by itself every time the laptop turns on (no need to open run.bat again).</li>
            <li>That's it — no settings to change. It's already connected to your site and keeps your laptop awake automatically.</li>
            <li>Come back to this page — the indicator above turns 🟢 <strong>green</strong>, then link with a code.</li>
          </ol>
          <p className="text-xs text-[#50564F] text-center">Everything else on your site works normally without this.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border-3 border-[#1A1C19] p-6 space-y-5" data-testid="wa-link-panel">
          {online && agentVer !== null && agentVer < CURRENT_AGENT_VER ? (
            /* Laptop is running an OLD capture app — pairing cannot work until it's updated */
            <div className="bg-[#FEF3C7] rounded-2xl border-3 border-[#B45309] p-5 space-y-3 text-center" data-testid="wa-update-required">
              <p className="text-2xl font-black text-[#92400E]">Update your laptop app</p>
              <p className="text-sm text-[#78350F]">Your office laptop is running an <strong>older capture app</strong> that can't refresh link codes (that's why the code is stuck). It takes one minute to fix:</p>
              <ol className="text-left text-sm text-[#78350F] font-semibold space-y-1 max-w-sm mx-auto list-decimal pl-5">
                <li>Tap the button below to download the latest app.</li>
                <li>On the office laptop: unzip it, then double-click <strong>run.bat</strong>.</li>
                <li>Close the old black window if it's still open.</li>
                <li>Come back here — this message disappears and you can get a fresh code.</li>
              </ol>
              <a href={`${API}/whatsapp/download-agent?base=${encodeURIComponent(BACKEND_URL)}`} download data-testid="wa-update-required-btn"
                className="yb-tactile inline-flex items-center gap-2 bg-[#B45309] text-white px-6 py-3 font-extrabold text-lg">
                <Download className="w-5 h-5" /> Download the latest app
              </a>
            </div>
          ) : (
          <>
          {/* PRIMARY: Link with a code — works from anywhere, no scanning */}
          <div className="bg-[#ECFDF5] rounded-2xl border-3 border-[#15803D] p-4 space-y-3" data-testid="wa-code-panel">
            <div className="flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-[#15803D]" />
              <p className="font-extrabold">Link with a code — works from anywhere</p>
            </div>
            <p className="text-xs text-[#50564F]">Do this right on your phone, even away from the office — as long as the laptop above is 🟢 online.</p>

            {/* Number entry is ALWAYS visible so refreshing a code never gets stuck */}
            <div className="space-y-2">
              <label className="text-sm font-bold text-[#1A1C19]">Your WhatsApp number</label>
              <div className="flex gap-2">
                <select value={dial} onChange={(e) => setDial(e.target.value)} data-testid="wa-country-select"
                  className="yb-tactile bg-white px-2 py-3 outline-none font-bold max-w-[46%]">
                  {COUNTRIES.map((co) => (
                    <option key={co.c + co.d} value={co.d}>{co.n} (+{co.d})</option>
                  ))}
                </select>
                <div className="flex items-center flex-1 yb-tactile bg-white px-3">
                  <span className="font-bold text-[#50564F] mr-1">+{dial}</span>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98765 43210" inputMode="numeric" data-testid="wa-phone-input"
                    className="flex-1 py-3 outline-none bg-transparent min-w-0" />
                </div>
              </div>
              <p className="text-xs text-[#50564F]">Type your number without the country code (leading zeros are fine).</p>
              <button onClick={pairWithCode} disabled={pairing || !online} data-testid="wa-pair-btn" className="yb-tactile w-full bg-[#25D366] text-white py-4 font-extrabold text-lg inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {pairing ? <Loader2 className="w-5 h-5 animate-spin" /> : pairCode ? <RefreshCw className="w-5 h-5" /> : <KeyRound className="w-5 h-5" />} {pairCode ? "Get a new code" : "Get my link code"}
              </button>
              {!online && <p className="text-xs text-[#DC2626] text-center font-bold">Laptop is offline — start the capture app in the office first.</p>}
            </div>

            {pairCode && (
              <div className="text-center space-y-2 border-t-2 border-[#BBF7D0] pt-3">
                <p className="text-sm text-[#50564F]">On your phone: WhatsApp → <strong>Settings → Linked Devices → Link a Device → Link with phone number instead</strong>, then type this code:</p>
                <p className="text-4xl font-black tracking-[0.25em] bg-white border-3 border-[#1A1C19] rounded-xl py-4" data-testid="wa-pair-code">{pairCode}</p>
                <p className="text-xs text-[#50564F]">Waiting for you to enter it… codes expire in a few minutes — if it stops working, tap “Get a new code”.</p>
              </div>
            )}
          </div>

          {/* SECONDARY: QR (advanced, needs a second screen) */}
          <div>
            <button onClick={() => { setShowQr((v) => !v); if (!showQr && status !== "qr") startLink(); }} data-testid="wa-qr-toggle"
              className="text-sm font-bold text-[#50564F] inline-flex items-center gap-2 hover:text-[#1A1C19]">
              <QrCode className="w-4 h-4" /> {showQr ? "Hide QR option" : "Advanced: scan a QR instead (needs a second screen)"}
            </button>
            {showQr && (
              <div className="text-center space-y-3 mt-3 bg-[#F8FAFC] rounded-2xl border-2 border-[#E5E5E0] p-4">
                {status === "qr" && qr ? (
                  <>
                    <p className="text-sm text-[#50564F]">Open this page on a <strong>second device</strong>, then scan from your phone: WhatsApp → Settings → Linked Devices → Link a Device.</p>
                    <img src={qr} alt="WhatsApp QR" className="mx-auto w-56 h-56 border-3 border-[#1A1C19] rounded-xl" data-testid="wa-qr" />
                    <p className="text-xs text-[#50564F]">Waiting for scan…</p>
                  </>
                ) : (
                  <>
                    <p className="font-bold text-[#50564F]">{status === "loading" ? "Checking…" : status === "reconnecting" ? "Reconnecting…" : "Preparing QR…"}</p>
                    <button onClick={startLink} disabled={linking || status === "loading"} data-testid="wa-link-btn" className="yb-tactile bg-white px-6 py-3 font-extrabold inline-flex items-center gap-2">
                      {linking ? <Loader2 className="w-5 h-5 animate-spin" /> : <QrCode className="w-5 h-5" />} Show QR code
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Re-download the (updated) capture app — needed to apply pairing fixes */}
          <div className="border-t-2 border-[#F0F0EC] pt-4 text-center space-y-2">
            <p className="text-xs text-[#50564F]">Code not refreshing or won't stay linked? Get the latest capture app, then run it on your office laptop (replaces the old one).</p>
            <a href={`${API}/whatsapp/download-agent?base=${encodeURIComponent(BACKEND_URL)}`} download data-testid="wa-update-btn"
              className="yb-tactile inline-flex items-center gap-2 bg-white px-4 py-2 font-bold">
              <Download className="w-4 h-4" /> Update the capture app
            </a>
          </div>
          </>
          )}
        </div>
      )}

      {/* Send a WhatsApp (Nexus outbound) */}
      <WASendComposer userId={userId} />

      {/* Deleted list */}
      {status === "connected" && (
        <div className="space-y-3">
          <p className="font-extrabold text-lg">Deleted messages ({deleted.length})</p>
          {deleted.length === 0 ? (
            <p className="text-[#50564F]">No deleted messages captured yet. When someone deletes a message sent to you, it will appear here.</p>
          ) : (
            deleted.map((m) => <DeletedCard key={m.msg_id} msg={m} userId={userId} uiLang={session.ui_lang} onDismiss={(id) => setDeleted((prev) => prev.filter((x) => x.msg_id !== id))} />)
          )}
        </div>
      )}
    </div>
  );
}
